import { AUTH_ENDPOINTS } from 'constants/apiEndPoints';
import ThrottledApi from './api/throttledApi';
import axios, { AxiosResponse } from 'axios';
import { getAPIBaseUrl } from 'utils';

const getUrl = getAPIBaseUrl();

// ===== AUTHENTICATION SERVICE =====
// Available functions for authentication:
// - requestOtp(identifier, options) - Request OTP via email, SMS, or phone call
// - verifyOtp(identifier, otp) - Verify OTP and login
// - register(data) - Register new user
//
// These are standard functions, NOT custom AI tools.
// Use them directly in your code with import statements.

// ===== POST OPERATIONS (ACTUALLY USED) =====

// Request OTP for login - used in login component
export const requestOtp = async (
  identifier: string,
  options: { authorizeOtp?: boolean; call?: boolean } = {}
) => {
  console.log('🔐 AuthService: Requesting OTP for identifier:', identifier);
  const data = {
    identifier,
    ...(options.authorizeOtp && { authorize_otp: true }),
    ...(options.call && { call: true })
  };

  try {
    const response = await axios.post(`${getUrl}${AUTH_ENDPOINTS.REQUEST_OTP}`, data) as AxiosResponse;

    console.log('🔐 AuthService: OTP requested successfully');
    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.log('🔐 AuthService: OTP request failed:', error);
    return {
      success: false,
      data: null,
      error: error.response?.data.detail || error.message
    };
  }
};

// Verify OTP and login - used in login component
export const verifyOtp = async (identifier: string, otp: string) => {
  console.log('🔐 AuthService: Verifying OTP for identifier:', identifier);
  const data = {
    identifier,
    otp: otp
  };

  try {
    const response = await axios.post(`${getUrl}${AUTH_ENDPOINTS.VERIFY_OTP}`, data) as AxiosResponse;

    console.log('🔐 AuthService: OTP verified successfully');
    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.log('🔐 AuthService: OTP verification failed:', error);
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message
    };
  }
};

// Register new user - used in registration component
export const register = async (userData: any) => {
  console.log('🔐 AuthService: Registering new user with data:', userData);
  try {
    const response = await axios.post(`${getUrl}${AUTH_ENDPOINTS.REGISTER}`, userData) as AxiosResponse;

    console.log('🔐 AuthService: User registered successfully');
    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.log('🔐 AuthService: User registration failed:', error);
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message
    };
  }
};

// Get SMS consent status - used in login component
export const getConsent = async (phoneNumber: string) => {
  console.log('🔐 AuthService: Getting SMS consent for phone:', phoneNumber);
  try {
    const response = await ThrottledApi.get(AUTH_ENDPOINTS.GET_CONSENT(phoneNumber)) as AxiosResponse;

    console.log('🔐 AuthService: SMS consent retrieved successfully');
    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.log('🔐 AuthService: SMS consent retrieval failed:', error);
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message
    };
  }
};

// Refresh access token - used in axios interceptor
export const refreshToken = async (refreshToken: string) => {
  console.log('🔐 AuthService: Refreshing token');
  try {
    const response = await axios.post(`${getUrl}${AUTH_ENDPOINTS.REFRESH_TOKEN}`, { refresh_token: refreshToken }) as AxiosResponse;

    console.log('🔐 AuthService: Token refreshed successfully', response);
    return {
      success: true,
      data: response.data,
      error: null,
      status: response.status
    };
  } catch (error: any) {
    console.log('🔐 AuthService: Token refresh failed:', error.response);
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message,
      status: error.response?.status
    };
  }
};

// ===== UTILITY METHODS =====

// Get current user details
export const getCurrentUser = async () => {
  console.log('🔐 AuthService: Getting current user');
  try {
    const response = await ThrottledApi.get(AUTH_ENDPOINTS.GET_CURRENT_USER) as AxiosResponse;

    console.log('🔐 AuthService: Current user retrieved successfully');
    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.log('🔐 AuthService: Current user retrieval failed:', error);
    return {
      success: false,
      data: null,
      error: error.response?.data || error.message
    };
  }
};

// Logout (clear tokens)
const getPostLogoutPath = () => process.env.REACT_APP_ENV === 'refactor' ? '/app' : '/';

export const logout = () => {
  console.log('🔐 AuthService: Logging out user');
  localStorage.clear();
  window.location.replace(getPostLogoutPath());
};

// Check if user is authenticated
export const isAuthenticated = () => {
  const token = localStorage.getItem('token');
  const refreshToken = localStorage.getItem('refresh_token');
  return !!(token && refreshToken);
};

// Get stored tokens
export const getTokens = () => {
  return {
    token: localStorage.getItem('token'),
    refreshToken: localStorage.getItem('refresh_token')
  };
};

// Set tokens after successful login
export const setTokens = (accessToken: string, refreshToken: string) => {
  console.log('🔐 AuthService: Setting tokens');
  localStorage.setItem('token', accessToken);
  localStorage.setItem('refresh_token', refreshToken);
};

// Export all auth functions as a single object for convenience
export const authService = {
  requestOtp,
  verifyOtp,
  register,
  getConsent,
  refreshToken,
  getCurrentUser,
  logout,
  isAuthenticated,
  getTokens,
  setTokens
};

export default authService; 
